import React, { useEffect, useState } from "react";
import "./AgentAccessPanel.css";
import ConfirmDeleteButton from "./ConfirmDeleteButton";
import {
  ApiToken,
  IssuedApiToken,
  buildMcpConnectCommand,
  createApiToken,
  getMcpEndpointUrl,
  listApiTokens,
  revokeApiToken,
} from "../utils/api";

/** Offered expiries. A token that never expires is a choice, not the default. */
const EXPIRY_OPTIONS = [
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "1 year", days: 365 },
  { label: "Never", days: 0 },
];

/**
 * Access for a user's own tools — an MCP client, a script — which cannot sign in
 * through a browser. The secret is shown once, here, because the server keeps
 * only its hash and genuinely cannot show it again.
 */
const AgentAccessPanel: React.FC = () => {
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [expiryDays, setExpiryDays] = useState(90);
  const [creating, setCreating] = useState(false);
  const [issued, setIssued] = useState<IssuedApiToken | null>(null);
  const [error, setError] = useState<string | null>(null);

  const endpoint = getMcpEndpointUrl();

  const load = async () => {
    try {
      setTokens(await listApiTokens());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim() || creating) return;

    setCreating(true);
    setError(null);
    try {
      setIssued(await createApiToken(name.trim(), expiryDays || undefined));
      setName("");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id: string) => {
    setError(null);
    try {
      await revokeApiToken(id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <section className="app-section">
      <div className="app-card">
        <h2 className="app-title">
          <i className="fas fa-robot" aria-hidden="true" /> Agent access
        </h2>
        <p className="agent-access-intro">
          Sieve speaks <abbr title="Model Context Protocol">MCP</abbr>, so an assistant can search
          everything your links turned into. Point a client at this endpoint and give it a token:
        </p>

        <CopyRow value={endpoint} label="MCP endpoint" />

        <form className="agent-access-form" onSubmit={handleCreate}>
          <label className="agent-access-label" htmlFor="token-name">
            New token
          </label>
          <div className="agent-access-inputs">
            <input
              id="token-name"
              type="text"
              value={name}
              maxLength={60}
              onChange={(event) => setName(event.target.value)}
              placeholder="What will use it? e.g. laptop Claude Code"
            />
            <select
              value={expiryDays}
              onChange={(event) => setExpiryDays(Number(event.target.value))}
              aria-label="Token expiry"
            >
              {EXPIRY_OPTIONS.map((option) => (
                <option key={option.days} value={option.days}>
                  {option.label}
                </option>
              ))}
            </select>
            <button type="submit" className="app-button agent-access-create" disabled={!name.trim() || creating}>
              {creating ? "Creating…" : "Create"}
            </button>
          </div>
        </form>

        {error && (
          <p className="agent-access-error">
            <i className="fas fa-triangle-exclamation" aria-hidden="true" /> {error}
          </p>
        )}

        {loading ? (
          <p className="agent-access-empty">Loading…</p>
        ) : tokens.length === 0 ? (
          <p className="agent-access-empty">No tokens yet.</p>
        ) : (
          <ul className="agent-access-list">
            {tokens.map((token) => (
              <li key={token.id}>
                <div className="agent-access-token">
                  <span className="agent-access-token-name">{token.name}</span>
                  <span className="agent-access-token-meta">
                    <code>{token.prefix}…</code> · {describeUsage(token)} · {describeExpiry(token)}
                  </span>
                </div>
                <ConfirmDeleteButton
                  onConfirm={() => handleRevoke(token.id)}
                  className="agent-access-revoke"
                  confirmClassName="agent-access-revoke--armed"
                  title="Revoke"
                  confirmTitle="Tap again to revoke"
                  ariaLabel={`Revoke ${token.name}`}
                  confirmAriaLabel={`Tap again to revoke ${token.name}`}
                />
              </li>
            ))}
          </ul>
        )}

        {issued && (
          <IssuedTokenModal issued={issued} endpoint={endpoint} onClose={() => setIssued(null)} />
        )}
      </div>
    </section>
  );
};

const IssuedTokenModal: React.FC<{
  issued: IssuedApiToken;
  endpoint: string;
  onClose: () => void;
}> = ({ issued, endpoint, onClose }) => (
  <div className="sync-modal-overlay" onClick={onClose}>
    <div
      className="sync-modal agent-access-modal"
      onClick={(event) => event.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="issued-token-title"
    >
      <h3 id="issued-token-title">“{issued.name}” is ready</h3>
      <p className="agent-access-warning">
        <i className="fas fa-triangle-exclamation" aria-hidden="true" /> Copy it now — only a hash is
        stored, so this is the last time it can be shown.
      </p>

      <CopyRow value={issued.token} label="Token" secret />
      <CopyRow
        value={buildMcpConnectCommand(endpoint, issued.token)}
        label="Add it to Claude Code"
        multiline
      />

      <button type="button" className="app-button agent-access-done" onClick={onClose}>
        Done
      </button>
    </div>
  </div>
);

const CopyRow: React.FC<{
  value: string;
  label: string;
  multiline?: boolean;
  secret?: boolean;
}> = ({ value, label, multiline, secret }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused (insecure origin, denied permission).
      // The value is on screen and selectable, so this is not worth an error.
      setCopied(false);
    }
  };

  return (
    <div className="agent-access-copy">
      <span className="agent-access-copy-label">{label}</span>
      <div className={`agent-access-copy-row ${multiline ? "is-multiline" : ""}`}>
        <code className={secret ? "is-secret" : undefined}>{value}</code>
        <button type="button" className="agent-access-copy-button" onClick={() => void copy()}>
          <i className={`fas ${copied ? "fa-check" : "fa-copy"}`} aria-hidden="true" />
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
};

const formatDate = (value: string): string =>
  new Date(value).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

const describeUsage = (token: ApiToken): string =>
  token.last_used_at ? `last used ${formatDate(token.last_used_at)}` : "never used";

const describeExpiry = (token: ApiToken): string => {
  if (!token.expires_at) return "no expiry";
  const expires = new Date(token.expires_at);
  return expires.getTime() < Date.now() ? "expired" : `expires ${formatDate(token.expires_at)}`;
};

export default AgentAccessPanel;
